/**
 * Form answer state helpers.
 *
 * The form holds answers in a flat map keyed by a composite of question key +
 * segment. Answers for hidden questions are kept in this map and never dropped
 * — re-showing a question restores its value (overview Phase 1 data
 * preservation).
 */
import {
  AnswerPayload,
  BuyerProfileAnswerEntry,
  MultiselectAnswer,
  QuestionDef,
  RangeAnswer,
  TextAnswer,
} from './types';
import { selectedValues } from './displayRules';

const SEP = ' ';

export type AnswersMap = Record<string, AnswerPayload>;

/** Composite key for the answers map. */
export const answerKey = (questionKey: string, segment?: string | null): string =>
  segment ? `${questionKey}${SEP}${segment}` : questionKey;

const parseKey = (key: string): { questionKey: string; segment: string | null } => {
  const idx = key.indexOf(SEP);
  if (idx === -1) return { questionKey: key, segment: null };
  return { questionKey: key.slice(0, idx), segment: key.slice(idx + 1) };
};

/** Build the form map from stored answer entries. */
export const entriesToMap = (entries: BuyerProfileAnswerEntry[]): AnswersMap => {
  const map: AnswersMap = {};
  for (const entry of entries) {
    map[answerKey(entry.questionKey, entry.segment)] = entry.answer;
  }
  return map;
};

/** ISO timestamp per composite key, for "last updated" stamps. */
export type TimestampMap = Record<string, string>;

/** Build the per-answer timestamp map from stored entries. */
export const entriesToTimestampMap = (
  entries: BuyerProfileAnswerEntry[]
): TimestampMap => {
  const map: TimestampMap = {};
  for (const entry of entries) {
    if (entry.updatedAt) map[answerKey(entry.questionKey, entry.segment)] = entry.updatedAt;
  }
  return map;
};

/**
 * The most recent timestamp across all of a question's segment rows — a
 * per-segment question shows one "last updated" for the whole card.
 */
export const latestTimestampForQuestion = (
  timestamps: TimestampMap,
  questionKey: string
): string | undefined => {
  let latest: string | undefined;
  const prefix = `${questionKey}${SEP}`;
  for (const [key, ts] of Object.entries(timestamps)) {
    if (key !== questionKey && !key.startsWith(prefix)) continue;
    if (!latest || ts > latest) latest = ts;
  }
  return latest;
};

/**
 * Earliest and latest timestamps across all answered questions — the span the
 * heading's freshness bar shows. Returns null when nothing has been saved yet.
 */
export const timestampRange = (
  timestamps: TimestampMap
): { earliest: string; latest: string } | null => {
  const values = Object.values(timestamps);
  if (values.length === 0) return null;
  let earliest = values[0];
  let latest = values[0];
  for (const ts of values) {
    if (ts < earliest) earliest = ts;
    if (ts > latest) latest = ts;
  }
  return { earliest, latest };
};

/** Flatten the form map back into entries for the save API. */
export const mapToEntries = (map: AnswersMap): BuyerProfileAnswerEntry[] =>
  Object.entries(map)
    .filter(([, answer]) => answer != null)
    .map(([key, answer]) => {
      const { questionKey, segment } = parseKey(key);
      return { questionKey, segment, answer };
    });

/**
 * Drop any answer keyed to a question the current config no longer knows about
 * (e.g. `revenue_types` after its removal). Applied both on initial load and
 * before save so legacy stored data doesn't poison the in-memory map and the
 * server's strict "Unknown question" rejection can't be tripped by it.
 */
export const dropUnknownQuestions = (
  map: AnswersMap,
  questions: QuestionDef[]
): AnswersMap => {
  const known = new Set(questions.map((q) => q.key));
  let next: AnswersMap | null = null;
  for (const key of Object.keys(map)) {
    const { questionKey } = parseKey(key);
    if (known.has(questionKey)) continue;
    next ??= { ...map };
    delete next[key];
  }
  return next ?? map;
};

/**
 * Active market segments drive per-segment question expansion.
 * 0 selected → render the question once with no segment (fallback);
 * ≥1 selected → render one copy per segment (just one column when there's
 * only one — the segment header is suppressed so it looks identical to the
 * legacy single-column layout).
 *
 * We intentionally do NOT collapse a single selected segment back to the
 * null-keyed column: doing so meant a user's per-segment answers got
 * orphaned every time they added/removed a second market segment, since
 * the storage key (`development_stage` vs `development_stage CI`) changed
 * underneath them.
 */
export const activeSegments = (map: AnswersMap): string[] =>
  selectedValues(map[answerKey('market_segment')]);

/**
 * The (segment) values a per-segment question should render for.
 * A non-per-segment question always renders once with `null`.
 */
export const segmentsForQuestion = (
  question: QuestionDef,
  map: AnswersMap
): (string | null)[] => {
  if (!question.perSegment) return [null];
  const segments = activeSegments(map);
  return segments.length >= 1 ? segments : [null];
};

/** Whether a single answer payload carries any actual input. */
const isAnswerProvided = (
  type: QuestionDef['type'],
  answer: AnswerPayload | undefined
): boolean => {
  if (answer == null) return false;
  switch (type) {
    case 'multiselect':
      // At least one option carries a preference (yes / maybe / no all count
      // as a deliberate answer).
      return Object.keys(answer as MultiselectAnswer).length > 0;
    case 'range': {
      const r = answer as RangeAnswer;
      return r.min != null || r.max != null || (r.gradations?.length ?? 0) > 0;
    }
    case 'text':
      return ((answer as TextAnswer).value ?? '').trim().length > 0;
    default:
      return false;
  }
};

/**
 * Whether a question has been answered. A per-segment question counts as
 * answered once any of its rendered segment columns carries input — partially
 * filled is still "started". Used by the form to flag sections needing input.
 */
export const isQuestionAnswered = (
  question: QuestionDef,
  map: AnswersMap
): boolean =>
  segmentsForQuestion(question, map).some((segment) =>
    isAnswerProvided(question.type, map[answerKey(question.key, segment)])
  );

/**
 * One-shot migration applied when the form loads stored answers: any
 * perSegment question that still carries a legacy null-segment answer
 * (written before per-segment rendering kicked in at ≥1 segment) is
 * copied to every currently-selected segment that doesn't already have
 * its own answer, then the null key is dropped. Without this, those
 * legacy answers would become invisible in the new rendering and would
 * still get re-saved as orphan rows.
 */
export const migrateLegacyNullSegmentAnswers = (
  map: AnswersMap,
  questions: QuestionDef[]
): AnswersMap => {
  const segments = activeSegments(map);
  if (segments.length === 0) return map;
  let next: AnswersMap | null = null;
  for (const q of questions) {
    if (!q.perSegment) continue;
    const nullKey = answerKey(q.key, null);
    const legacy = map[nullKey];
    if (legacy == null) continue;
    next ??= { ...map };
    for (const segment of segments) {
      const segKey = answerKey(q.key, segment);
      if (next[segKey] == null) next[segKey] = legacy;
    }
    delete next[nullKey];
  }
  return next ?? map;
};

/**
 * Answers keyed by question key — the input to display rules.
 *
 * Per-segment questions store one row per market segment, so a naive
 * `segment == null` filter would hide their answers entirely once a market
 * segment is picked. For display-rule purposes we therefore merge the
 * per-segment rows into a synthetic multiselect under the bare key: an
 * option counts as "selected" if any segment marked it `yes` or `maybe`.
 * That makes "show sub-question if parent has option X" work the same way
 * for per-segment parents as for non-per-segment ones, regardless of how
 * many segments the buyer has picked.
 *
 * Geography is the exception: its only display-rule consumer is the Community
 * Solar utility-zones panel (`optionGroupFilter: { question: 'geography' }`),
 * so its synthetic value must reflect *only* the Community Solar segment's
 * state picks. Merging in the (often-broad) C&I / Utility geography would
 * surface every utility zone regardless of which Community Solar states were
 * actually selected.
 */
const COMMUNITY_SOLAR_SEGMENT = 'COMMUNITY_SOLAR';

export const answersByKeyForDisplay = (
  map: AnswersMap,
  questions: QuestionDef[]
): Record<string, unknown> => {
  const perSegmentKeys = new Set(
    questions.filter((q) => q.perSegment).map((q) => q.key)
  );
  const byKey: Record<string, unknown> = {};
  for (const [key, answer] of Object.entries(map)) {
    const { questionKey, segment } = parseKey(key);
    if (segment == null) {
      byKey[questionKey] = answer;
      continue;
    }
    if (!perSegmentKeys.has(questionKey)) continue;
    // Only the Community Solar segment feeds geography's display value (see
    // the doc comment above) — skip the other segments' geography rows.
    if (questionKey === 'geography' && segment !== COMMUNITY_SOLAR_SEGMENT) {
      continue;
    }
    const merged = (byKey[questionKey] as Record<string, string>) ?? {};
    for (const [val, pref] of Object.entries(
      (answer as Record<string, string>) ?? {}
    )) {
      if (pref === 'yes' || pref === 'maybe') merged[val] = 'yes';
    }
    byKey[questionKey] = merged;
  }
  return byKey;
};
